import { Button, Chip, DateSpan, FileItem, GenericSuspense, Metadata, Avatar, useFileUrl, useTranslationRoute, useTranslations, useUrlParamsValueToString, useRuntimeEnv, useUserProfilePicture, useNumberFormat } from "@harea/component"
import { TTimeOff } from "@harea/component"
import { Link, Outlet, useParams } from "react-router-dom"
import { ReactNode, useCallback, useMemo, useState } from "react"
import { useValidateDayOffMutation } from "hooks/useValidateDayOffMutation"
import { useRefuseDayOffMutation } from "hooks/useRefuseDayOffMutation"
import { useAppointmentDetailsEditLink } from "hooks/useAppointmentDetailsEditLink"
import { useTimeOffQuery } from "hooks/useTimeOffQuery"
import { AppointmentDetailsType } from "components/AppointmentDetailsType"
import { useContractManagerQuery } from "hooks/useContractManagerQuery"
import moment from "moment"
import { MaternityDetails } from "components/MaternityDetails"

export const DayoffMissingDetails = (props: Props) => {
    const [
        CREATOR,
        STATUS,
        CLOSED,
        OPENED,
        CREATED_AT,
        DATE_BEGIN,
        DATE_END,
        LABELS,
        USERS,
        DESCRIPTION,
        ATTACHMENT,
        METADATA,
        ARCHIVED,
        EDIT,
        MILESTONE,
        USER,
        REPETITION,
        VALIDATE,
        APPROVE,
        REFUSE,
        EMPLOYEE,
        POSITION,
        REMAINDER,
        REASON,
        EMPLOYEE_DETAILS,
        TOTAL_DAYS,
        TOTAL_HOURS,
        PAYROLL_DEDUCTION_AMOUNT,
        TREATMENT,
        TREATMENT_EXCEPTION,
        TREATMENT_PAYROLL,
        TREATMENT_NEGATIVE_BALANCE
    ] = useTranslations(i18n)
    const { id } = useParams()
    const editLink = useAppointmentDetailsEditLink(props.type)
    const fileUrl = useFileUrl()
    const tr = useTranslationRoute()
    const formatCurrency = useNumberFormat({ style: "currency", currency: "MGA" })
    const { data, invalidateQuery: invalidateContractQuery } = useContractManagerQuery(props?.employee?.id, props?.type?.designation as any, true);

    const userProfilePictureUrl = useUserProfilePicture()

    const getEndIcon = useCallback((approval) => {
        if (approval?.isApproved) {
            return "fa-circle-check text-success"
        }

        if (approval?.isRefused) {
            return "fa-circle-xmark text-danger"
        }
    }, [props])
    
    const remainingSolde = (data?.leaveBalance as any)?.congeSolde?.toString()

    const duration = useMemo(() => {
        if (!props?.dateBegin || !props?.dateEnd) return undefined;
        return props?.countWorkingDays
    }, [props?.dateBegin, props?.dateEnd, props?.countWorkingDays]);

    const treatmentLabel = useMemo(() => {
        const p: any = props;
        if (p?.unpaidLeaveMode === 'EXCEPTION' || p?.isExceptionalPaid || p?.exception) return TREATMENT_EXCEPTION;
        if (p?.unpaidLeaveMode === 'PAYROLL' || p?.deductFromPay) return TREATMENT_PAYROLL;
        if (p?.unpaidLeaveMode === 'BALANCE' || p?.negativeBalance) return TREATMENT_NEGATIVE_BALANCE;
        return null;
    }, [props]);

    const designation = (props as any)?.type?.designation
    const isMaternity = designation === "MATERNAL" || designation === "MATERNAL_ASSISTANCE"

    return (
    <div className="flex flex-col gap-10">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col">
              <div className="flex justify-between flex-wrap">
                <h3 className="flex gap-4 items-center">
                  <AppointmentDetailsType {...props} />
                </h3>
                {props.isEditable && (
                  <div className="flex gap-2">
                    {/* <Link to={editLink}>
                      <Button
                        className="btn-default"
                        startIcon="fa-pen"
                        label={EDIT}
                      />
                    </Link> */}
                    {props.toolbox}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-between">
              {
                props?.labels?.length > 0 && (
                <Metadata
                  label={LABELS}
                  value={
                    props.labels?.length > 0 ? (
                      <div className="flex gap-2 flex-wrap">
                        {props.labels?.map((item, index) => (
                          <Chip
                            key={index}
                            className="bg-white"
                            label={item.value}
                            color={item.color}
                          />
                        ))}
                      </div>
                    ) : undefined
                  }
                />
                )
              }
              {
                props?.userCreator && (
                  <div className="flex gap-4 flex-wrap">
                    <Metadata
                      label={CREATOR}
                      value={
                        <Link to={tr(`admin/user/${props?.userCreator?.employee?.id}`)} target="_blank">
                          <Chip
                            userId={props?.userCreator?.id}
                            label={props?.userCreator?.employee?.fullName}
                          />
                        </Link>
                      }
                    />
                  </div>
                )
              }
            </div>
          </div>
    
          <div>
            <div className="flex flex-col gap-2">
              <h3 className="mb-2">{EMPLOYEE_DETAILS}</h3>
              <div className="grid grid-cols-2 gap-2 divide-x-2">
                <div className="px-4">
                  {props.employee && (
                    <div className="flex items-start gap-2">
                      <Link
                        to={`${tr(`admin/user/${props?.employee?.id}`)}`}
                        target="_blank"
                        className="flex items-center gap-3"
                      >
                        <Avatar
                          src={userProfilePictureUrl(props?.employee?.id)}
                          fullName={props?.employee?.fullName}
                          size="lg"
                        />
                      </Link>
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {props?.employee?.fullName}
                        </span>
                        <span className="text-xs text-muted">
                          {(props?.employee as any)?.jobPosition?.value ??
                            (props?.employee as any)?.position ??
                            props?.employee?.department?.designation ??
                            ""}
                        </span>
                        <div className="mt-2 flex flex-col">
                          <div className="flex gap-3 items-center">
                            <span className="text-xs text-muted">{POSITION}</span>
                            <span className="font-medium">
                              {(data?.jobPosition as any)?.value ?? "-"}
                            </span>
                          </div>
                          <div className="flex gap-3 items-center">
                            <span className="text-xs text-muted">{REMAINDER}</span>
                            <span className="font-medium">
                              {remainingSolde}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-4 px-4">
                  {props.dateBegin && (
                    <Metadata
                      label={DATE_BEGIN}
                      className="grid grid-cols-[130px_1fr] gap-4 items-center"
                      value={
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          <Chip
                            startIcon="fa-clock"
                            className="bg-default shrink-0"
                            label={<DateSpan value={props.dateBegin} format="LL" />}
                          />
                          {(props.hourBegin || props.hourEnd) && (
                            <span className="text-sm text-muted whitespace-nowrap">
                              {props.hourBegin}
                            </span>
                          )}
                        </div>
                      }
                    />
                  )}

                  {props.dateEnd && (
                    <Metadata
                      label={DATE_END}
                      className="grid grid-cols-[130px_1fr] gap-4 items-center"
                      value={
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          <Chip
                            startIcon="fa-clock"
                            className="bg-default shrink-0"
                            label={<DateSpan value={props.dateEnd} format="LL" />}
                          />
                          {props.hourEnd && (
                            <span className="text-sm text-muted whitespace-nowrap">
                              {props.hourEnd}
                            </span>
                          )}
                        </div>
                      }
                    />
                  )}

                  {duration !== undefined && (
                      <Metadata
                          label={moment(props?.dateBegin).isSame(moment(props?.dateEnd), 'day') ? TOTAL_HOURS : TOTAL_DAYS}
                          className="grid grid-cols-[130px_1fr] gap-4 items-center"
                          value={<span className="font-medium">{duration.toString()}</span>}
                      />
                  )}
                  {treatmentLabel && (
                      <Metadata
                          label={TREATMENT}
                          className="grid grid-cols-[130px_1fr] gap-4 items-center"
                          value={<span className="font-medium">{treatmentLabel}</span>}
                      />
                  )}
                  {(props as any)?.payrollDeductionAmount != null && (
                      <Metadata
                          label={PAYROLL_DEDUCTION_AMOUNT}
                          className="grid grid-cols-[130px_1fr] gap-4 items-center"
                          value={<span className="font-medium text-danger">{formatCurrency((props as any)?.payrollDeductionAmount)}</span>}
                      />
                  )}
                </div>
              </div>
    
              <div className="flex gap-4 flex-wrap">
                {props.approvals?.length > 0 && (
                  <Metadata
                    label={USERS}
                    className="flex-col items-start"
                    value={
                      <div className="flex gap-2 flex-wrap">
                        {props.approvals.map((item) => (
                          <Link to={tr(`admin/user/${item?.approver?.id}`)} target="_blank">
                            <Chip
                              userId={item?.approver?.id}
                              label={item?.approver?.fullName}
                              endIcon={getEndIcon(item)}
                            />
                          </Link>
                        ))}
                      </div>
                    }
                  />
                )}
              </div>
    
              <div className="flex gap-4">
                {props.createdAt && (
                  <Metadata
                    icon="fa-clock"
                    value={<DateSpan value={props.createdAt} />}
                    title={CREATED_AT}
                  />
                )}
              </div>
            </div>
          </div>
    
          {props.reason && (
            <div className="flex flex-col gap-2">
              <h3>{REASON}</h3>
              <div className="text-sm text-muted break-words">{props.reason}</div>
            </div>
          )}

          {isMaternity && (
            <MaternityDetails {...((props as any).maternityLeave || props)} employee={props.employee} leave={props} />
          )}
    
          {props.files?.length > 0 && (
            <div className="flex flex-col gap-2">
              <h3>{ATTACHMENT}</h3>
    
              <div className="flex gap-4 flex-wrap">
                {props.files?.map((item, index) => (
                  <a href={fileUrl(item.id)} target="_blank">
                    <FileItem {...item} key={index} />
                  </a>
                ))}
              </div>
            </div>
          )}
    
          <GenericSuspense>
            <Outlet />
          </GenericSuspense>
        </div>
    )
}

type Props = Partial<TTimeOff> & {
    toolbox?: ReactNode
    disableDetailsQuery?: boolean
}

const i18n = [
    'std_creator',
    'std_status',
    'std_closed',
    'std_opened',
    'std_created_at',
    'BSMRH.Calendar.TimeOff.Create.Form.Label.DateAppointmentBegin',
    'BSMRH.Calendar.TimeOff.Create.Form.Label.DateAppointmentEnd',
    'std_label',
    'Harea.Project.Calendar.Details.Users.Label',
    'Harea.Project.Calendar.Details.Description.Label',
    "std_justification",
    "std_metadata",
    "std_archived",
    'std_edit',
    'Harea.Project.Calendar.Details.Milestone.Label',
    'std_user',
    "Harea.Vacation.Calendar.Event.Create.Vacation.Form.Label.Repetition",
    'std_validate',
    'std_approve',
    'std_refuse',
    'std_employee',
    'std_position',
    'std_solde_remainder',
    'std_reason',
    "BSMRH.Project.Dayoff.Demand.Details.EmployeeDetails.Label",
    "std_total_days",
    "std_total_hours",
    "Harea.Project.DayOff.Detail.PayrollDeductionAmount",
    "Harea.Project.DayOff.Detail.Treatment",
    "Harea.Project.DayOff.Detail.Treatment.Exception",
    "Harea.Project.DayOff.Detail.Treatment.Payroll",
    "Harea.Project.DayOff.Detail.Treatment.NegativeBalance"
]
